import M from 'messages';
import moment from 'moment';
import {
  chanelMapping,
  receiveMarketingEmailsMapping,
  receiveNewsEmailsMapping,
  receiveEventsEmailsMapping,
  activityTypeMapping,
  shortInterestsMapping,
} from 'constants/winlover-filters';
import { pad } from './helper';

const createUsersTableData = (id, name, usersRole, email, isApproved, lastLoginDate, serviceProviderId, image) => ({
  id,
  name,
  usersRole,
  email,
  isApproved,
  lastLoginDate,
  serviceProviderId,
  image,
});

const adaptUsersTableData = data => {
  const dataList = [];
  data.forEach(item => {
    dataList.push(
      createUsersTableData(
        pad(item.id, 3),
        item.name,
        M.get(`usersRoles.${item.usersRole.name}`),
        item.email,
        item.isApproved,
        item.lastLoginDate ? moment(item.lastLoginDate).format('DD.MM.YYYY, HH:mm') : '',
        item.serviceProvider ? item.serviceProvider.name : '',
        item.image,
      ),
    );
  });
  return dataList;
};

// eslint-disable-next-line camelcase
const createSPTableData = (
  id,
  sortingOrder,
  name,
  type,
  isQrPartner,
  isLibrary,
  isApproved,
  approved,
  country,
  email,
  userId,
  created_at,
) => ({
  id,
  sortingOrder,
  name,
  type,
  isQrPartner,
  isLibrary,
  isApproved,
  approved,
  country,
  email,
  userId,
  created_at,
});

const adaptSPTableData = response => {
  const dataList = [];
  response.forEach(item => {
    dataList.push(
      createSPTableData(
        pad(item.id, 3),
        item.sortingOrder,
        item.name,
        item.type,
        item.isQrPartner,
        item.isLibrary,
        item.user?.isApproved,
        item.user?.isApproved ? 'Approved' : 'Not approved',
        item.country?.name || '',
        item.user?.email || '',
        item.user?.id,
        item.created_at ? moment(item.created_at).format('DD/MM/YY') : '',
      ),
    );
  });
  return dataList;
};

const createWineListsTableData = (
  menuName,
  menuItemsCount,
  partnerName,
  id,
  isDefault,
  active,
  shouldResetWineRating,
  createdAt,
) => ({
  menuName,
  menuItemsCount,
  partnerName,
  id,
  isDefault,
  active,
  shouldResetWineRating,
  createdAt,
});

const adaptWineListsTableData = data => {
  const dataList = [];
  data.forEach(item => {
    dataList.push(
      createWineListsTableData(
        item.menuName,
        item.menuItemsCount,
        item.serviceProvider?.name || '',
        pad(item.id, 3),
        item.isDefault,
        item.active,
        item.shouldResetWineRating,
        item.createdAt ? moment(item.createdAt).format('DD/MM/YY') : '',
      ),
    );
  });
  return dataList;
};

const createWinesTableData = (
  id,
  partnerName,
  producer,
  name,
  country,
  color,
  isSuperVino,
  year,
  status,
  active,
  createdAt,
  serviceProviderId,
  menus,
) => ({
  id,
  partnerName,
  producer,
  name,
  country,
  color,
  isSuperVino,
  year,
  status,
  active,
  createdAt,
  serviceProviderId,
  isDraft: status === 'draft',
  statusLabel: status === 'draft' ? 'Pending' : !active ? 'Locked' : menus.length === 0 ? 'Unlisted' : 'Live',
  lists: { count: menus.length, names: menus.map(list => list.menuName) },
});

const adaptWinesTableData = data => {
  const dataList = [];
  data.forEach(item => {
    dataList.push(
      createWinesTableData(
        pad(item.id, 3),
        item.serviceProvider.name,
        item.producer,
        item.name,
        item.country?.name || '',
        item.color,
        item.isSuperVino,
        item.year,
        item.status,
        item.active,
        item.createdAt ? moment(item.createdAt).format('DD/MM/YY') : '',
        item.serviceProviderId,
        item.menus,
      ),
    );
  });
  return dataList;
};

/** Wine library browse page: columns only (producer, name, country, year, color, SuperV) + row keys for actions */
const adaptWineLibraryBrowseTableData = data => {
  if (!data?.length) {
    return [];
  }
  return data.map(item => ({
    id: item.id,
    serviceProviderId: item.serviceProviderId,
    active: item.active,
    producer: item.producer ?? '',
    name: item.name ?? '',
    country: item.country?.name ?? '',
    year: item.year ?? '',
    color: item.color ?? '',
    isSuperVino: Boolean(item.isSuperVino),
  }));
};

const serviceProvidersOptionData = data => data.map(item => ({ value: item.id, label: item.name, title: item.name }));

const createWineLabelsTableData = (
  id,
  serviceProviderId,
  name,
  country,
  producer,
  color,
  year,
  energyKcal,
  status,
  createdAt,
  active,
  partnerName,
  partnerIsActive,
) => ({
  id,
  serviceProviderId,
  name,
  country,
  producer,
  color,
  year,
  energyKcal,
  status,
  createdAt,
  active,
  partnerName,
  partnerIsActive,
});

const adaptWineLabelsTableData = data => {
  const dataList = [];
  data.forEach(item => {
    dataList.push(
      createWineLabelsTableData(
        pad(item.id, 3),
        item.serviceProvider.id,
        item.name,
        item.country?.name || '',
        item.producer,
        item.color,
        item.year,
        item.energyKcal,
        item.status === 'in_market' ? 'yes' : 'no',
        item.createdAt ? moment(item.createdAt).format('DD/MM/YY') : '',
        item.active,
        item.serviceProvider.name,
        item.serviceProvider.accountStatus === 'active',
      ),
    );
  });
  return dataList;
};

const createWineLoversTableData = (
  id,
  name,
  email,
  image,
  provider,
  country,
  state,
  userType,
  registrationType,
  interests,
  receiveMarketingEmails,
  receiveNewsEmails,
  receiveEventsEmails,
  lastLogin,
  createdAt,
) => ({
  id,
  name,
  email,
  image,
  provider,
  country,
  state,
  userType,
  registrationType,
  interests,
  receiveMarketingEmails,
  receiveNewsEmails,
  receiveEventsEmails,
  lastLogin,
  createdAt,
});

const adaptWineLoversTableData = data => {
  const dataList = [];
  data.forEach(item => {
    const formattedInterests = item.interests
      ? item.interests
          .split(',')
          .map(interest => shortInterestsMapping[interest.trim()] || interest.trim())
          .join(', ')
      : '';
    dataList.push(
      createWineLoversTableData(
        pad(item.id, 3),
        item.name,
        item.email,
        item.image,
        chanelMapping[item.provider] || item.provider,
        item.country,
        item.state,
        item.userType,
        item.registrationType,
        formattedInterests,
        receiveMarketingEmailsMapping[item.receiveMarketingEmails] || '',
        receiveNewsEmailsMapping[item.receiveNewsEmails] || '',
        receiveEventsEmailsMapping[item.receiveEventsEmails] || '',
        item.lastLogin ? moment(item.lastLogin).format('DD.MM.YYYY, HH:mm') : '',
        item.createdAt ? moment(item.createdAt).format('DD.MM.YYYY, HH:mm') : '',
      ),
    );
  });
  return dataList;
};

const adaptWineLoverDetailsData = data => ({
  id: pad(data.id, 3),
  name: data.name,
  email: data.email,
  image: data.image,
  provider: chanelMapping[data.provider] || data.provider,
  country: data.country,
  state: data.state,
  lastLogin: data.lastLogin ? moment(data.lastLogin).format('DD.MM.YYYY, HH:mm') : '',
  createdAt: data.createdAt ? moment(data.createdAt).format('DD.MM.YYYY, HH:mm') : '',
  userType: data.userType,
  registrationType: data.registrationType,
  interests: data.interests,
  receiveMarketingEmails: receiveMarketingEmailsMapping[data.receiveMarketingEmails] || '',
  receiveNewsEmails: receiveNewsEmailsMapping[data.receiveNewsEmails] || '',
  receiveEventsEmails: receiveEventsEmailsMapping[data.receiveEventsEmails] || '',
});

// eslint-disable-next-line camelcase
const createWineLoverActivitiesTableData = (
  row_id,
  user_id,
  user_name,
  user_email,
  user_image,
  channel,
  role,
  registration_type,
  marketing_enabled,
  receive_news_emails,
  receive_events_emails,
  activity_timestamp,
  activity_type,
  partner_name,
  menu_name,
  wine_name,
  vintage,
  producer,
  rating,
  country,
  state,
) => ({
  row_id,
  user_id,
  user_name,
  user_email,
  user_image,
  channel,
  role,
  registration_type,
  marketing_enabled,
  receive_news_emails,
  receive_events_emails,
  activity_timestamp,
  activity_type,
  partner_name,
  menu_name,
  wine_name,
  vintage,
  producer,
  rating,
  country,
  state,
});

const adaptWineLoverActivitiesTableData = data => {
  const dataList = [];
  data.forEach(item => {
    dataList.push(
      createWineLoverActivitiesTableData(
        item.row_id,
        item.user_id ? pad(item.user_id, 3) : '',
        item.user_name,
        item.user_email,
        item.user_image,
        chanelMapping[item.channel] || item.channel,
        item.role,
        item.registration_type,
        receiveMarketingEmailsMapping[item.marketing_enabled] || '',
        receiveNewsEmailsMapping[item.receive_news_emails] || '',
        receiveEventsEmailsMapping[item.receive_events_emails] || '',
        item.activity_timestamp?.value ? moment(item.activity_timestamp?.value).format('DD.MM.YYYY, HH:mm') : '',
        item.activity_type,
        item.partner_name,
        item.menu_name,
        item.wine_name,
        item.vintage,
        item.producer,
        item.rating && item.rating !== 'N/A' ? String(Math.floor(Number(item.rating) * 10) / 10) : item.rating,
        item.country,
        item.state,
      ),
    );
  });
  return dataList;
};

const adaptWineLoversActivitiesPartnerFilter = data =>
  data.map(item => ({ value: item.partner_name, label: item.partner_name }));

const adaptWineLoversActivitiesMenuFilter = data =>
  data.map(item => ({ value: item.menu_name, label: item.menu_name }));

const adaptWineLoversActivitiesWineFilter = data =>
  data.map(item => ({ value: item.wine_name, label: item.wine_name }));

const adaptWineLoversActivitiesVintageFilter = data => [
  ...data.map(item => ({ value: item.vintage, label: item.vintage })),
  {
    value: 'notSet',
    label: M.get('table.wineLoversActivities.filter.interests.notSet'),
  },
];

const adaptWineLoversActivitiesProducerFilter = data =>
  data.map(item => ({ value: item.producer, label: item.producer }));

const adaptWineLoversActivitiesCountryFilter = data => [
  ...data.map(item => ({ value: item.country, label: item.country })),
  {
    value: 'notSet',
    label: M.get('table.wineLoversActivities.filter.interests.notSet'),
  },
];

const adaptWineLoversActivitiesStateFilter = data => [
  ...data.map(item => ({ value: item.state, label: item.state })),
  {
    value: 'notSet',
    label: M.get('table.wineLoversActivities.filter.interests.notSet'),
  },
];

const adaptWineLoversCountriesFilter = data => [
  ...data.map(item => ({ value: item.country, label: item.country })),
  {
    value: 'notSet',
    label: M.get('table.wineLoversActivities.filter.interests.notSet'),
  },
];

const adaptWineLoversStatesFilter = data => [
  ...data.map(item => ({ value: item.state, label: item.state })),
  {
    value: 'notSet',
    label: M.get('table.wineLoversActivities.filter.interests.notSet'),
  },
];

const adaptWineLoverActivityDetailsData = data => ({
  user_name: data.user_name,
  user_email: data.user_email,
  user_image: data.user_image,
  user_id: data.user_id ? pad(data.user_id, 3) : '',
  channel: chanelMapping[data.channel] || data.channel,
  role: data.role,
  registration_type: data.registration_type,
  interests: data.interests,
  marketing_enabled: receiveMarketingEmailsMapping[data.marketing_enabled] || '',
  receive_news_emails: receiveNewsEmailsMapping[data.receive_news_emails] || '',
  receive_events_emails: receiveEventsEmailsMapping[data.receive_events_emails] || '',
  activity_timestamp: data.activity_timestamp?.value
    ? moment(data.activity_timestamp?.value).format('DD.MM.YYYY, HH:mm')
    : '',
  activity_type: activityTypeMapping[data.activity_type] || data.activity_type,
  partner_name: data.partner_name,
  menu_name: data.menu_name,
  wine_name: data.wine_name,
  vintage: data.vintage,
  producer: data.producer,
  rating: data.rating,
  country: data.country,
  state: data.state,
});

const createCampaignsTableData = (
  id,
  name,
  type,
  status,
  createdAt,
  sentAt,
  audience,
  targetRecipients,
  receivedCount,
  openedCount,
  clickedCtaCount,
) => ({
  id,
  name,
  type,
  status,
  createdAt,
  sentAt,
  audience,
  targetRecipients,
  receivedCount,
  openedCount,
  clickedCtaCount,
});

const adaptCampaignsTableData = data => {
  const getAudienceSummary = audienceDefinition => {
    if (!audienceDefinition) return '';
    if (audienceDefinition.partnerAudience && audienceDefinition.partnerAudience.length > 0) {
      const parts = [];
      if (audienceDefinition.partnerAudience.includes('followers')) parts.push(M.get('pages.partnerAudienceFollowers'));
      if (audienceDefinition.partnerAudience.includes('rated')) parts.push(M.get('pages.partnerAudienceRated'));
      if (audienceDefinition.partnerAudience.includes('saved')) parts.push(M.get('pages.partnerAudienceSaved'));
      return parts.join(', ');
    }
    return '';
  };
  return data.map(item =>
    createCampaignsTableData(
      pad(item.id, 3),
      item.name,
      item.type,
      item.status,
      item.createdAt ? moment(item.createdAt).format('DD.MM.YYYY, HH:mm') : M.get('campaigns.notApplicable'),
      item.sentAt ? moment(item.sentAt).format('DD.MM.YYYY, HH:mm') : M.get('campaigns.notApplicable'),
      getAudienceSummary(item.audienceDefinition),
      item.targetRecipients || M.get('campaigns.notApplicable'),
      item.receivedCount,
      item.openedCount,
      item.clickedCtaCount ?? M.get('campaigns.notApplicable'),
    ),
  );
};

const createTasteimonialsTableData = (
  id,
  createdAt,
  note,
  noteResponse,
  partnerName,
  producer,
  wine,
  vintage,
  wineList,
  wineLoverName,
  wineLoverId,
  status,
) => ({
  id,
  createdAt,
  note,
  noteResponse,
  partnerName,
  producer,
  wine,
  vintage,
  wineList,
  wineLoverName,
  wineLoverId,
  status,
});

const adaptTasteimonialsTableData = data => {
  return data.map(item =>
    createTasteimonialsTableData(
      item.id,
      item.createdAt ? moment(item.createdAt).format('DD.MM.YYYY, HH:mm') : '',
      item.note,
      item.noteResponse || '',
      item.serviceProvider?.name || '',
      item.menuItem?.producer || '',
      item.menuItem?.name || '',
      item.menuItem?.year || '',
      item.menu?.menuName || '',
      item.wineLoverName,
      item.wineLoverId,
      item.status,
    ),
  );
};

export {
  adaptUsersTableData,
  adaptSPTableData,
  adaptWineListsTableData,
  adaptWinesTableData,
  adaptWineLibraryBrowseTableData,
  serviceProvidersOptionData,
  adaptWineLabelsTableData,
  adaptWineLoversTableData,
  adaptWineLoverDetailsData,
  adaptWineLoverActivitiesTableData,
  adaptWineLoversActivitiesPartnerFilter,
  adaptWineLoversActivitiesMenuFilter,
  adaptWineLoversActivitiesWineFilter,
  adaptWineLoversActivitiesVintageFilter,
  adaptWineLoverActivityDetailsData,
  adaptWineLoversCountriesFilter,
  adaptWineLoversStatesFilter,
  adaptWineLoversActivitiesProducerFilter,
  adaptWineLoversActivitiesCountryFilter,
  adaptWineLoversActivitiesStateFilter,
  adaptCampaignsTableData,
  adaptTasteimonialsTableData,
};
